import { useState, useEffect } from "react";
import { Search, Plus, Trash2, Users, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "../api.js";
import { useConfirm } from "../components/ConfirmDialog.jsx";
import { usePatients } from "../contexts/PatientsContext.jsx";
import NewPatientModal from "../components/NewPatientModal.jsx";
import { useAuth } from "../contexts/AuthContext.jsx";
import { useConnectivity } from "../contexts/ConnectivityContext.jsx";
import { useDebouncedValue } from "../lib/useDebouncedValue.js";
import PatientAlias from "../components/PatientAlias.jsx";

const PAGE_SIZE = 60;

export default function PatientsPage({ onSelectPatient }) {
  const { user } = useAuth();
  const { addPatient, removePatient } = usePatients();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [query, setQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [locations, setLocations] = useState([]);
  const [showNewPatientModal, setShowNewPatientModal] = useState(false);
  const { confirm, ConfirmDialogElement } = useConfirm();
  const { lastSyncAt } = useConnectivity();

  const debouncedQuery = useDebouncedValue(query, 300);
  const hasFilters = !!(debouncedQuery || locationFilter);

  const load = () => {
    setLoading(true);
    return api.listPatients({ limit: PAGE_SIZE, offset: 0, q: debouncedQuery, location: locationFilter })
      .then(r => { setItems(r.items); setTotal(r.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [debouncedQuery, locationFilter]);

  // Refreshes the list after any sync (manual or the backend's automatic background sync) —
  // a sync can pull in a patient created at another location. Was previously tied to a local
  // "Синхронизировать" button's onSynced callback; now reacts to sync happening anywhere.
  useEffect(() => { if (lastSyncAt) load(); }, [lastSyncAt]);

  useEffect(() => { api.listLocations().then(setLocations).catch(() => {}); }, []);

  const loadMore = () => {
    setLoadingMore(true);
    api.listPatients({ limit: PAGE_SIZE, offset: items.length, q: debouncedQuery, location: locationFilter })
      .then(r => { setItems(prev => [...prev, ...r.items]); setTotal(r.total); })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  const handleCreatePatient = async (form) => {
    try {
      const created = await addPatient(form);
      setItems(prev => [created, ...prev]);
      setTotal(t => t + 1);
      setShowNewPatientModal(false);
    } catch (err) {
      throw new Error(err.response?.data?.error || "Ошибка создания");
    }
  };

  const deletePatient = async (e, id) => {
    e.stopPropagation();
    if (!(await confirm("Удалить пациента? Его сканы и отчёты останутся в разделе «Сканирования», но потеряют привязку к пациенту.", { danger: true }))) return;
    await removePatient(id);
    setItems(prev => prev.filter(p => p.id !== id));
    setTotal(t => t - 1);
  };

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white flex-shrink-0 gap-3">
        <h2 className="text-lg font-bold text-slate-900">Пациенты</h2>
        <div className="flex items-center gap-2">
          {locations.length > 1 && (
            <select
              className="h-8 rounded-md border border-slate-200 bg-slate-50 px-2 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
              value={locationFilter}
              onChange={e => setLocationFilter(e.target.value)}
            >
              <option value="">Все точки</option>
              {locations.map(loc => <option key={loc.id} value={loc.name}>{loc.name}</option>)}
            </select>
          )}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              className="h-8 w-60 pl-8 text-xs border-slate-200 bg-slate-50"
              placeholder="Поиск по имени..."
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          <Button size="sm" className="gap-1.5" onClick={() => setShowNewPatientModal(true)}>
            <Plus className="h-3.5 w-3.5" />
            Добавить пациента
          </Button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 px-5 py-6 text-sm text-slate-400">
          <div className="h-4 w-4 rounded-full border-2 border-slate-300 border-t-blue-600 animate-spin" />
          Загрузка пациентов...
        </div>
      )}

      {!loading && total === 0 && !hasFilters && (
        <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
          <Users className="h-8 w-8 text-slate-300" />
          <p className="text-sm font-semibold text-slate-700">Нет пациентов</p>
          <p className="text-xs text-slate-400">Добавьте первого пациента, чтобы начать работу</p>
          <Button size="sm" className="gap-1.5 mt-2" onClick={() => setShowNewPatientModal(true)}>
            <Plus className="h-3.5 w-3.5" />
            Добавить пациента
          </Button>
        </div>
      )}

      {!loading && total === 0 && hasFilters && (
        <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
          <Search className="h-8 w-8 text-slate-300" />
          <p className="text-sm font-semibold text-slate-700">Ничего не найдено</p>
          <p className="text-xs text-slate-400">Нет пациентов с именем «{query}»</p>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-2">
        {items.map(patient => (
          <div
            key={patient.id}
            className="group flex items-center gap-3 px-4 py-3 bg-white border border-slate-200 rounded-xl cursor-pointer transition hover:shadow-md hover:border-slate-300 hover:-translate-y-px"
            onClick={() => onSelectPatient(patient.id)}
          >
            <div className="flex items-center justify-center h-10 w-10 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 text-blue-600 text-base font-bold flex-shrink-0">
              {patient.name[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900">{patient.name}<PatientAlias name={patient.name} alias={patient.alias} /></div>
              <div className="text-xs text-slate-400 mt-0.5">
                Добавлен: {new Date(patient.createdAt).toLocaleDateString("ru-RU")}
                {patient.location && (
                  <span className="inline-flex items-center gap-1 ml-2 text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
                    <MapPin className="h-2.5 w-2.5" />
                    {patient.location}
                  </span>
                )}
              </div>
            </div>
            {user?.isCentral && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-slate-300 opacity-0 group-hover:opacity-100 hover:text-red-600 hover:bg-red-50"
                onClick={e => deletePatient(e, patient.id)}
                title="Удалить"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        ))}
        {!loading && items.length < total && (
          <div className="flex justify-center mt-1">
            <Button variant="ghost" size="sm" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? "Загрузка..." : `Показать ещё (${total - items.length})`}
            </Button>
          </div>
        )}
      </div>

      {showNewPatientModal && (
        <NewPatientModal
          onClose={() => setShowNewPatientModal(false)}
          onCreate={handleCreatePatient}
        />
      )}
      <ConfirmDialogElement />
    </div>
  );
}
